import type { Service } from '../../../redux/types';
import { getTopServicesForUser } from '../../../utils/serviceTrackUtils.ts';
import { getCurrentUserEmail } from '../../../utils/tokenUtils.ts';
import { getPinnedShortcuts } from '../../../utils/shortcutsUtils.ts';
import type { Shortcut } from '../shortcuts/type.ts';
import i18n from '../../../i18n.ts';
import { SEARCH_BAR_MIN_QUERY_LENGTH } from './SearchBar.tsx';

export type DisplayedServices = {
  mostUsed?: Service[];
  services?: Service[];
  shortcuts?: Shortcut[];
};

const getDisplayedServices = (services: Service[], searchValue: string): DisplayedServices => {
  const userEmail = getCurrentUserEmail();
  const currentLang = i18n.languages[0];

  const mostUsed = userEmail ? getTopServicesForUser(userEmail, 5) : [];

  if (!searchValue || searchValue.length < SEARCH_BAR_MIN_QUERY_LENGTH) {
    const mostUsedServices = mostUsed.map(s => services.find(service => service.path === s.path));

    const mostUsedPaths = mostUsedServices.map(service => service?.path);
    const filteredServices = services
      .filter(service => !mostUsedPaths.includes(service.path))
      .sort((a, b) => (a.title ?? '').localeCompare(b.title ?? ''));

    return {
      mostUsed: mostUsedServices.filter(Boolean) as Service[],
      services: filteredServices,
    };
  }

  const shortcuts = getPinnedShortcuts();

  const searchValueLower = searchValue.toLowerCase();

  const filteredServices = services.filter(service => {
    const titleLower = service.title.toLowerCase();
    const descriptionLower = service.description.toLowerCase();

    return titleLower.includes(searchValueLower) || descriptionLower.includes(searchValueLower);
  });

  const filteredShortcuts = shortcuts.filter(shortcut => {
    const shortcutTitle = (shortcut.titles?.[currentLang] ?? shortcut.titles?.['en'] ?? shortcut.title).toLowerCase();

    return shortcutTitle.includes(searchValueLower);
  });

  return {
    services: filteredServices,
    shortcuts: filteredShortcuts,
  };
};

export default getDisplayedServices;
